import {
  Box,
  Container,
  Stack,
  Typography,
  Tabs,
  Tab,
  styled,
  Badge,
  alpha,
} from "@mui/material";
import { BackButton, BackDropLoading } from "@/organism";
import Router from "@/constants/router";
import useDetail from "./useDetail";
import CompanyStamp from "@/templates/CompanyStamp";
import { useState, useEffect } from "react";
import {
  Article as ArticleIcon,
  CloudDownload as CloudDownloadIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material";
import useAdminBoardList from "./useAdminBoardList";
import { getAdminNmByToken } from "@/apis/admin";

// 스타일링된 탭 컴포넌트
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 64,
  fontSize: "0.9rem",
  fontWeight: 500,
  textTransform: "none",
  transition: "0.2s",
  "&.Mui-selected": {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&.MuiTab-root": {
    padding: "12px 24px",
  },
  "& .MuiTab-iconWrapper": {
    marginRight: 8,
    marginBottom: "0 !important",
  },
}));

// 탭 컨테이너
const TabContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  boxShadow: `0 1px 3px 0 ${alpha(theme.palette.common.black, 0.04)}`,
  position: "relative",
  zIndex: 1,
  marginBottom: theme.spacing(3),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    backgroundColor: theme.palette.divider,
  },
}));

/**
 * 도장 상세 정보 페이지
 * @description 4개의 탭으로 구성된 도장 상세 정보 페이지입니다.
 * - 도장 정보: 기본 정보 및 설정
 * - 자료 게시판: 게시물 목록
 * - 대시보드: 통계 정보
 * - 관리자 로그인 정보: 관리자 접속 통계
 */
export const Detail = () => {
  const { isLoading, companyStampInfo } = useDetail();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [currentAdminId, setCurrentAdminId] = useState<string>("");

  // 회사 ID를 companyStampInfo에서 가져옵니다
  const comId = companyStampInfo?.comId || "";

  // 현재 로그인한 관리자 정보 가져오기
  useEffect(() => {
    const fetchAdminInfo = async () => {
      try {
        const response = await getAdminNmByToken();
        // 관리자 이름을 가져온 후, 게시판 항목과 매칭시켜 ID를 설정
        // 여기서는 게시물 목록이 로드되지 않았으므로 임시로 빈 문자열 설정
        if (response) {
          setCurrentAdminId(response.admNm); // 일단 관리자 이름을 ID로 사용
        }
      } catch (error) {
        console.error("관리자 정보 조회 중 오류 발생:", error);
      }
    };

    fetchAdminInfo();
  }, []);

  // API에서 게시판 목록을 가져옵니다
  const {
    isLoading: isBoardLoading,
    boardList,
    page,
    limit,
    totalCount,
    handlePageChange,
  } = useAdminBoardList({
    comId: comId,
    initialPage: 1,
    initialLimit: 10,
  });

  // 게시물 목록을 가져온 후 관리자 ID 매칭
  useEffect(() => {
    if (boardList.length > 0 && currentAdminId) {
      const matchingItem = boardList.find(
        (item) => item.admName === currentAdminId
      );
      if (matchingItem) {
        setCurrentAdminId(matchingItem.admIdntfcId);
      }
    }
  }, [boardList, currentAdminId]);

  // 탭 변경 핸들러
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Material UI의 Pagination 컴포넌트와 호환되도록 이벤트 핸들러 변환
  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    handlePageChange(value);
  };

  return (
    <Container maxWidth={"xl"} sx={{ px: 2 }}>
      <BackDropLoading isLoading={isLoading} />
      <Box display={"flex"} justifyContent={"space-between"} alignItems={"end"}>
        <Stack direction={"column"} gap={1}>
          <BackButton to={Router.CompanyStamp}>도장 목록</BackButton>
          <Box display="flex" alignItems="baseline" gap={2}>
            <Typography
              component={"h1"}
              variant="h5"
              fontWeight={"bold"}
              gutterBottom
            >
              도장 상세 정보
            </Typography>
          </Box>
          {companyStampInfo?.comNm && (
            <Typography
              variant="h6"
              color="primary"
              fontWeight={"700"}
              gutterBottom
            >
              도장이름: {companyStampInfo.comNm}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* 탭 메뉴 */}
      <TabContainer>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTabs-indicator": {
              height: 2,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <StyledTab
            label="도장 정보"
            icon={<ArticleIcon />}
            iconPosition="start"
          />
          <StyledTab
            label={
              <Badge
                color="primary"
                badgeContent={totalCount}
                invisible={totalCount === 0}
                sx={{ "& .MuiBadge-badge": { right: -14, top: 4 } }}
              >
                자료 게시판
              </Badge>
            }
            icon={<CloudDownloadIcon />}
            iconPosition="start"
          />
          <StyledTab
            label="통계분석"
            icon={<AnalyticsIcon />}
            iconPosition="start"
          />
          <StyledTab
            label="관리자 로그인 정보"
            icon={<AdminPanelSettingsIcon />}
            iconPosition="start"
          />
        </Tabs>
      </TabContainer>

      {/* 탭 컨텐츠 */}
      {companyStampInfo && (
        <Box
          sx={{
            mt: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            backgroundColor: "background.paper",
            boxShadow: `0 1px 3px 0 ${alpha("#000", 0.04)}`,
            overflow: "hidden",
            transition: "all 0.3s ease",
            animation: activeTab >= 2 ? "fadeIn 0.3s ease" : "none",
            "@keyframes fadeIn": {
              "0%": { opacity: 0.7, transform: "translateY(10px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* 도장 정보 탭 */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <CompanyStamp.Detail companyStampInfo={companyStampInfo} />
            </Box>
          )}

          {/* 자료 요청 탭 */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <CompanyStamp.Board
                comId={comId}
                isLoading={isBoardLoading}
                boardList={boardList}
                page={page}
                limit={limit}
                totalCount={totalCount}
                onPageChange={handlePaginationChange}
                currentAdminId={currentAdminId}
              />
            </Box>
          )}

          {/* 통계분석 탭 - Hook으로 데이터 관리 */}
          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              <CompanyStamp.Statistics comId={comId} />
            </Box>
          )}

          {/* 관리자 로그인 정보 탭 - Hook으로 데이터 관리 */}
          {activeTab === 3 && (
            <Box sx={{ p: 3 }}>
              <CompanyStamp.AdminLoginStats comId={comId} />
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};
